import { useProfile } from '@/hooks/useCommonQueries';

/**
 * Production fiscal identity gate (spec §9.2 / INDEX C3).
 *
 * A tenant on a paid lane (runtime_access `paid_active` | `paid_past_due` —
 * authoritative, computed by `runtime_access_for_profile` on the server) must
 * provision its production fiscal identity (NIF + razón social, extracted from
 * the AEAT P12) before issuing production invoices. Trial / verified / blocked
 * tenants are NOT gated here (trial runs on the sandbox lane; paid_blocked is
 * handled by the subscription gate).
 *
 * The two predicates are explicit backend-authoritative fields — never inferred
 * from `subscription.status` / `profile.status` strings (codex F8). Shared by
 * `ConsoleLayout` and `StoreLayout` so the gate cannot be bypassed via the
 * `/stores/:id/*` routes, and so the decision lives in a single source.
 */
export function useFiscalSetupGate(): { needsFiscalSetup: boolean } {
  const { data: profileData } = useProfile();
  const access = profileData?.runtime_access;
  const onPaidLane = access?.kind === 'paid_active' || access?.kind === 'paid_past_due';
  const needsFiscalSetup =
    !!profileData &&
    onPaidLane &&
    profileData.production_fiscal_identity.state === 'UNPROVISIONED';
  return { needsFiscalSetup };
}
